import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Controller, useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const chunk = (array, size) => {
  const chunked = [];
  for (let i = 0; i < array.length; i += size) {
    chunked.push(array.slice(i, i + size));
  }
  return chunked;
};

const FormBuilder = ({
  formConfig,
  onChange,
  onSubmit,
  submitButtonText = "Submit",
  hideSubmitButton = false,
  values, // Add values prop
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [formData, setFormData] = useState({});

  // Watch all form fields
  const watchAllFields = watch();

  // Handle individual field changes
  const handleFieldChange = (fieldName, value) => {
    const numericFields = [
      "kmCharge",
      "waitingTime",
      "bufferTime",
      "extraKmCharge",
      "graceKm",
      "extraTimeCharge",
      "graceTime",
    ];

    const updatedData = {
      ...formData,
      [fieldName]: numericFields.includes(fieldName)
        ? parseFloat(value) || 0
        : value,
    };

    setFormData(updatedData);
    onChange?.(updatedData);
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...watchAllFields,
    }));
  }, [watchAllFields]);

  useEffect(() => {
    // Initialize formData with default values from formConfig
    const initialData = {};
    formConfig.formField.forEach((section) => {
      section.formFields.forEach((field) => {
        initialData[field.name] = field.defaultValue || "";
      });
    });
    setFormData(initialData);
  }, [formConfig]);

  // Add useEffect to handle incoming values
  useEffect(() => {
    if (values) {
      // Set values for each field
      Object.keys(values).forEach((fieldName) => {
        setValue(fieldName, values[fieldName]);
      });

      // Update local form data state
      setFormData((prevData) => ({
        ...prevData,
        ...values,
      }));
    }
  }, [values, setValue]);

  const handleNumberInput = (e) => {
    e.target.addEventListener("wheel", (e) => e.preventDefault(), {
      passive: false,
    });
  };

  const renderFormField = (field) => {
    const fieldValue = values?.[field.name] || "";

    switch (field.inputType.toLowerCase()) {
      case "text":
      case "number":
      case "email":
      case "password":
        return (
          <TextField
            fullWidth
            label={field.label}
            type={field.inputType}
            value={
              field.inputType.toLowerCase() == "number" && fieldValue > 0
                ? fieldValue
                : ""
            }
            {...register(field.name, field.validation)}
            onChange={(e) => {
              const rawValue = e.target.value;
              const numericValue = parseFloat(rawValue);

              // Prevent negative or zero values
              const value = numericValue > 0 ? numericValue : "";

              setValue(field.name, value, { shouldValidate: true }); // force validation
              handleFieldChange(field.name, value);
            }}
            onFocus={(e) => {
              if (field.inputType === "number") {
                handleNumberInput(e);
              }
            }}
            inputProps={{
              min: 1,
              step: "any",
            }}
            error={!!errors[field.name]}
            helperText={errors[field.name]?.message}
          />
        );

      case "select":
        return (
          <Select
            fullWidth
            label={field.label}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            error={!!errors[field.name]}
            helperText={errors[field.name]?.message}
          >
            {field.options?.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );

      case "radio":
        return (
          <FormControl component="fieldset">
            <Controller
              name={field.name}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    handleFieldChange(field.name, e.target.value);
                  }}
                >
                  {field.options?.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </FormControl>
        );

      case "autocomplete":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple={field.multiple}
                options={field.options || []}
                value={value}
                onChange={(_, newValue) => {
                  onChange(newValue);
                  handleFieldChange(field.name, newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={field.label} />
                )}
              />
            )}
          />
        );

      case "richtext":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <ReactQuill
                theme="snow"
                value={value}
                onChange={(content) => {
                  onChange(content);
                  handleFieldChange(field.name, content);
                }}
              />
            )}
          />
        );

      case "custom":
        return field.customComponent || null;

      default:
        return null;
    }
  };

  // Add state for slabs
  const [slabs, setSlabs] = useState([]);

  // Update handleSlabFieldChange function
  const handleSlabFieldChange = (slabId, field, value) => {
    const updatedSlabs = slabs.map((slab) =>
      slab.id === slabId ? { ...slab, [field]: value } : slab,
    );

    setSlabs(updatedSlabs);

    // Format slabs for API
    const formattedSlabs = updatedSlabs.map((slab) => ({
      upto_km: parseFloat(slab.upto),
      base_rate_per_km: parseFloat(slab.kmCharge),
    }));

    // Update form data with formatted slabs
    const updatedFormData = {
      ...formData,
      slabs: updatedSlabs,
      rateConfig: formattedSlabs,
    };

    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const handleAddSlab = () => {
    const newSlab = {
      id: Date.now(),
      upto: "",
      kmCharge: "",
    };

    setSlabs([...slabs, newSlab]);

    // Update form data
    const updatedFormData = {
      ...formData,
      slabs: [...slabs, newSlab],
    };

    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const handleRemoveSlab = (slabId) => {
    setSlabs(slabs.filter((slab) => slab.id !== slabId));
  };

  const renderSlabSection = (section) => {
    return (
      <div className="form-section">
        <h3>{section.title}</h3>

        {slabs.map((slab) => (
          <div key={slab.id} className="slab-row">
            <div className="form-col">
              <TextField
                label="Upto (km)"
                type="number"
                value={slab.upto}
                onChange={(e) =>
                  handleSlabFieldChange(slab.id, "upto", e.target.value)
                }
                placeholder="Enter distance"
                required
                error={!slab.upto}
                helperText={!slab.upto ? "Required" : ""}
              />
            </div>
            <div className="form-col">
              <TextField
                label="/km Charge (₹)"
                type="number"
                value={slab.kmCharge}
                onChange={(e) =>
                  handleSlabFieldChange(slab.id, "kmCharge", e.target.value)
                }
                placeholder="Enter charge"
                required
                error={!slab.kmCharge}
                helperText={!slab.kmCharge ? "Required" : ""}
              />
            </div>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleRemoveSlab(slab.id)}
            >
              Remove
            </Button>
          </div>
        ))}

        <Button
          variant="contained"
          color="primary"
          onClick={handleAddSlab}
          sx={{ mt: 2 }}
        >
          Add Slab
        </Button>
      </div>
    );
  };

  //Package section
  const [packages, setPackages] = useState([]);
  const [expandedPackages, setExpandedPackages] = useState(new Set());

  const createNewPackage = () => ({
    id: Date.now().toString(),
    name: "",
    duration: "",
    durationType: "",
    maxDistanceKms: "",
    cost: "",
    additionalCharges: {
      extraKm: {
        enabled: false,
        graceKm: "",
        extraKmCharge: "",
      },
      extraTime: {
        enabled: false,
        graceTime: "",
        extraTimeCharge: "",
      },
    },
  });

  const handleAddPackage = () => {
    const newPackage = createNewPackage();
    setPackages([...packages, newPackage]);
    setExpandedPackages((prev) => new Set([...prev, newPackage.id]));

    // Update form data
    const updatedFormData = {
      ...formData,
      packages: [...packages, newPackage],
    };
    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const handleRemovePackage = (packageId) => {
    const updatedPackages = packages.filter((pkg) => pkg.id !== packageId);
    setPackages(updatedPackages);
    setExpandedPackages((prev) => {
      const newSet = new Set(prev);
      newSet.delete(packageId);
      return newSet;
    });

    // Update form data
    const updatedFormData = {
      ...formData,
      packages: updatedPackages,
    };
    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const handlePackageFieldChange = (packageId, field, value) => {
    const updatedPackages = packages.map((pkg) =>
      pkg.id === packageId ? { ...pkg, [field]: value } : pkg,
    );
    setPackages(updatedPackages);

    // Update form data
    const updatedFormData = {
      ...formData,
      packages: updatedPackages,
    };
    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const handleAdditionalChargeChange = (packageId, section, field, value) => {
    const updatedPackages = packages.map((pkg) =>
      pkg.id === packageId
        ? {
            ...pkg,
            additionalCharges: {
              ...pkg.additionalCharges,
              [section]: {
                ...pkg.additionalCharges[section],
                [field]: value,
              },
            },
          }
        : pkg,
    );
    setPackages(updatedPackages);

    // Update form data
    const updatedFormData = {
      ...formData,
      packages: updatedPackages,
    };
    setFormData(updatedFormData);
    onChange?.(updatedFormData);
  };

  const togglePackageExpansion = (packageId) => {
    setExpandedPackages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return newSet;
    });
  };

  const renderPackageSection = (section) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">{section.title}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddPackage}
            startIcon={<span>+</span>}
          >
            Add Package
          </Button>
        </Box>

        {packages.map((pkg, index) => (
          <Paper key={pkg.id} sx={{ mb: 2, border: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                Package {index + 1}
                {pkg.name && (
                  <span style={{ color: "#666", fontWeight: "normal" }}>
                    {" "}
                    - {pkg.name}
                  </span>
                )}
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={() => togglePackageExpansion(pkg.id)}
                  sx={{ mr: 1 }}
                >
                  {expandedPackages.has(pkg.id) ? "▲" : "▼"}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemovePackage(pkg.id)}
                >
                  <DeleteIcon />
                </Button>
              </Box>
            </Box>

            {/* Package Content */}
            {expandedPackages.has(pkg.id) && (
              <Box sx={{ p: 3 }}>
                {/* Basic Package Fields */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Package Name"
                      value={pkg.name}
                      onChange={(e) =>
                        handlePackageFieldChange(pkg.id, "name", e.target.value)
                      }
                      placeholder="Enter package name"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cost"
                      type="number"
                      value={pkg.cost}
                      onChange={(e) =>
                        handlePackageFieldChange(pkg.id, "cost", e.target.value)
                      }
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <span style={{ marginRight: "8px" }}>₹</span>
                        ),
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Duration"
                      type="number"
                      value={pkg.duration}
                      onChange={(e) =>
                        handlePackageFieldChange(
                          pkg.id,
                          "duration",
                          e.target.value,
                        )
                      }
                      placeholder="Enter duration"
                      inputProps={{ min: 1 }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Duration Type</InputLabel>
                      <Select
                        value={pkg.durationType}
                        onChange={(e) =>
                          handlePackageFieldChange(
                            pkg.id,
                            "durationType",
                            e.target.value,
                          )
                        }
                        label="Duration Type"
                      >
                        <MenuItem value="Min">Minutes</MenuItem>
                        <MenuItem value="Hour">Hours</MenuItem>
                        <MenuItem value="Day">Days</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Maximum KMs"
                      type="number"
                      value={pkg.maxDistanceKms}
                      onChange={(e) =>
                        handlePackageFieldChange(
                          pkg.id,
                          "maxDistanceKms",
                          e.target.value,
                        )
                      }
                      placeholder="Enter maximum kilometers (optional)"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>

                {/* Additional Charges Section */}
                <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Additional Charges (Optional)
                  </Typography>

                  {/* Extra KM Charge */}
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: "#f9f9f9" }}>
                    <FormControlLabel
                      control={
                        <input
                          type="checkbox"
                          checked={pkg.additionalCharges.extraKm.enabled}
                          onChange={(e) =>
                            handleAdditionalChargeChange(
                              pkg.id,
                              "extraKm",
                              "enabled",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Extra KM Charge"
                    />

                    {pkg.additionalCharges.extraKm.enabled && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Grace KM"
                            type="number"
                            value={pkg.additionalCharges.extraKm.graceKm}
                            onChange={(e) =>
                              handleAdditionalChargeChange(
                                pkg.id,
                                "extraKm",
                                "graceKm",
                                e.target.value,
                              )
                            }
                            placeholder="Enter grace kilometers"
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="/km Charge"
                            type="number"
                            value={pkg.additionalCharges.extraKm.extraKmCharge}
                            onChange={(e) =>
                              handleAdditionalChargeChange(
                                pkg.id,
                                "extraKm",
                                "extraKmCharge",
                                e.target.value,
                              )
                            }
                            placeholder="Enter charge per km"
                            InputProps={{
                              startAdornment: (
                                <span style={{ marginRight: "8px" }}>₹</span>
                              ),
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Paper>

                  {/* Extra Time Charge */}
                  <Paper sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
                    <FormControlLabel
                      control={
                        <input
                          type="checkbox"
                          checked={pkg.additionalCharges.extraTime.enabled}
                          onChange={(e) =>
                            handleAdditionalChargeChange(
                              pkg.id,
                              "extraTime",
                              "enabled",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label="Extra Time Charge"
                    />

                    {pkg.additionalCharges.extraTime.enabled && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Grace Time (min)"
                            type="number"
                            value={pkg.additionalCharges.extraTime.graceTime}
                            onChange={(e) =>
                              handleAdditionalChargeChange(
                                pkg.id,
                                "extraTime",
                                "graceTime",
                                e.target.value,
                              )
                            }
                            placeholder="Enter grace time in minutes"
                            inputProps={{ min: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="/min Charge"
                            type="number"
                            value={
                              pkg.additionalCharges.extraTime.extraTimeCharge
                            }
                            onChange={(e) =>
                              handleAdditionalChargeChange(
                                pkg.id,
                                "extraTime",
                                "extraTimeCharge",
                                e.target.value,
                              )
                            }
                            placeholder="Enter charge per minute"
                            InputProps={{
                              startAdornment: (
                                <span style={{ marginRight: "8px" }}>₹</span>
                              ),
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  const DEFAULT_GROUP_SIZE = 4; // Controls how many fields appear in one row

  const renderSubsection = (subsection, index) => {
    if (subsection.type === "slab") {
      return renderSlabSection(subsection);
    }

    const groupSize = subsection.groupSize || DEFAULT_GROUP_SIZE;

    return (
      <Box
        key={index}
        sx={{
          mb: 3,
        }}
      >
        {subsection.title && (
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              color: "#666",
              fontWeight: 500,

              fontSize: "0.875rem",
              letterSpacing: "0.5px",
            }}
          >
            {subsection.title}
          </Typography>
        )}
        <Grid container spacing={2}>
          {subsection.formFields.map((field, fieldIndex) => (
            <Grid
              item
              xs={12}
              sm={12 / Math.min(groupSize, subsection.formFields.length)}
              key={fieldIndex}
            >
              {renderFormField(field)}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderFormSection = (section, index) => {
    // First check for special section types
    // if (section.type === "slab") {
    //   return renderSlabSection(section);
    // }
    if (section.type === "package") {
      return renderPackageSection(section);
    }

    const groupSize = section.groupSize || DEFAULT_GROUP_SIZE;

    return (
      <Box
        key={index}
        className={
          section.title === "Basic Configuration" ? "basic-config-section" : ""
        }
      >
        {section.title && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.title}
          </Typography>
        )}

        {/* Render main section fields */}
        {section.formFields && section.formFields.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {section.formFields.map((field, fieldIndex) => (
              <Grid
                item
                xs={12}
                sm={12 / Math.min(groupSize, section.formFields.length)}
                key={fieldIndex}
              >
                {renderFormField(field)}
              </Grid>
            ))}
          </Grid>
        )}

        {/* Render subsections */}
        {section.subsections &&
          section.subsections.map((subsection, subsectionIndex) =>
            renderSubsection(subsection, subsectionIndex),
          )}
      </Box>
    );
  };

  const handleFormSubmit = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    onSubmit?.(data);
  };

  // Update the getAllSections function to include subsections
  const getAllSections = () => {
    const sections = JSON.parse(JSON.stringify(formConfig.formField)); // Deep clone to avoid mutations

    // Add Slab or Package sections
    if (["Rental", "Outstation"].includes(formData.rideType)) {
      // if (formData.rateType === 'slab') {
      //   sections.push({
      //     title: "Slab-wise Charges",
      //     type: "slab",
      //     formFields: []
      //   });
      // }
      if (formData.rateType === "package") {
        sections.push({
          title: "Package Charges",
          type: "package",
          formFields: [],
        });
      }
    }

    // Add all additional sections
    // sections.push(...getAdditionalSections());

    return sections;
  };

  // Add styles for the form sections
  const sectionStyles = {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {getAllSections().map((section, index) => (
        <div key={index} style={sectionStyles}>
          {renderFormSection(section, index)}
        </div>
      ))}
      {!hideSubmitButton && (
        <Button type="submit" variant="contained" color="primary">
          {submitButtonText}
        </Button>
      )}
    </form>
  );
};

export default FormBuilder;
